import type { PvpSeat } from './states';

/**
 * Who is in a duel, named so that six of them can be.
 *
 * ## What this file is and is not
 *
 * It is VOCABULARY. Nothing here runs a match, moves a ship or decides a
 * winner - it says what a participant is, what a team is, which slots a ruleset
 * has, and how the two-seat duel that ships today maps onto that. The
 * simulation, the room, the database and the socket still speak seats; they are
 * generalised on top of this, not by it.
 *
 * ## Why a participant id rather than an index
 *
 * A roster is a list and a list reorders. Keying anything by position means the
 * first time a room sends its participants in another order - or one dies and
 * is dropped - every piece of state inherits somebody else's. The renderer
 * learned that already (`pvp/view.ts`); this is the same answer for the
 * authoritative side, so one identity survives the lobby, the ticket, the
 * socket attachment, the snapshot, a reconnection, every frame, the result and
 * the audit trail.
 *
 * The id is DERIVED from team and slot rather than random. That is deliberate:
 * a random id would have to be stored, looked up and kept in step across five
 * boundaries, and two of those boundaries are a signed ticket and a persisted
 * snapshot. `a1` is stable by construction, reproducible from a database row,
 * and readable in a log - and it cannot be forged into somebody else's seat,
 * because what authorises a participant is the signature around it rather than
 * the string itself.
 *
 * ## And why the 1x1 keeps its seats
 *
 * `PvpSeat` is not being retired. It is the shape the wire, the database and a
 * signed ticket already speak, and rewriting all three at once is how a working
 * two-player duel becomes a broken six-player one. The 1x1's participant ids
 * are exactly `a1` and `b1`, and {@link pvpSeatToRef} / {@link pvpRefToSeat}
 * are the only place that correspondence is written down.
 */

/* -------------------------------------------------------------------------- */
/*  Teams and slots                                                           */
/* -------------------------------------------------------------------------- */

declare const PVP_TEAM_BRAND: unique symbol;

/**
 * The two sides. The same letters the seats use, and NOT the same type.
 *
 * ## Why this is branded, and what the brand actually buys
 *
 * A team is not a seat: with three ships a side, "team a" names a group and
 * "seat a" named a person. The first version of this file declared them as two
 * aliases of `'a' | 'b'` and the commit message claimed TypeScript would keep
 * them apart. **It does not.** Two aliases of one union are the same type, and
 * a probe assigning each to the other compiled without a word.
 *
 * So the team carries a brand. `const team: PvpTeam = 'a'` is now an error, and
 * a `PvpSeat` cannot become a team by being passed along - which is the
 * direction that matters, because it is the one where a two-seat assumption
 * would silently survive into a six-participant room.
 *
 * ## What it deliberately does not buy
 *
 * The reverse. A branded type is an intersection, and an intersection is
 * assignable to its own constituents, so a `PvpTeam` still satisfies a
 * `PvpSeat`. Branding the seat as well would reach the wire schemas, the
 * database and a signed ticket - which this round must not touch - so that
 * direction is governed by FUNCTIONS instead: {@link pvpRefToSeat} and
 * {@link pvpOutcomeToWinnerSeat} both refuse rather than convert whenever the
 * answer would be a lie, and they are the only two places that convert at all.
 *
 * Saying this plainly matters more than the brand: the previous claim was
 * wrong, and a guarantee somebody believes and does not have is worse than one
 * they know they lack.
 */
export type PvpTeam = ('a' | 'b') & { readonly [PVP_TEAM_BRAND]: 'pvp-team' };

/**
 * The one cast in the file, and the only way a team is made.
 *
 * Everything else - the constant below, the parser, the seat adapter - goes
 * through here, so "where does a team come from" has a single answer.
 */
function asTeam(letter: 'a' | 'b'): PvpTeam {
  return letter as PvpTeam;
}

export const PVP_TEAMS: readonly PvpTeam[] = Object.freeze([asTeam('a'), asTeam('b')]);

/** A team from an untrusted string, or `null`. The runtime half of the brand. */
export function pvpParseTeam(value: string): PvpTeam | null {
  return value === 'a' || value === 'b' ? asTeam(value) : null;
}

export function pvpOtherTeam(team: PvpTeam): PvpTeam {
  return team === 'a' ? asTeam('b') : asTeam('a');
}

/** Where in a team a participant sits. One-based, and never an array index. */
export const PVP_TEAM_SLOTS = [1, 2, 3] as const;
export type PvpTeamSlot = (typeof PVP_TEAM_SLOTS)[number];

/* -------------------------------------------------------------------------- */
/*  Rulesets                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Which shape of match this is.
 *
 * A ruleset is not a count. It is the thing a room, a ticket, a lobby and a
 * database row all have to agree about BEFORE anybody joins, because it decides
 * how many slots exist, how many participants a start requires, and what a
 * winner means. Carrying the number six around instead would leave every one of
 * those places free to disagree about what six was for.
 */
export const PVP_RULESETS = ['duel_1v1', 'team_3v3'] as const;
export type PvpRuleset = (typeof PVP_RULESETS)[number];

/** How many slots each team has under a ruleset. */
export function pvpSlotsPerTeam(ruleset: PvpRuleset): 1 | 3 {
  return ruleset === 'duel_1v1' ? 1 : 3;
}

/** Every slot a team may fill under a ruleset, in order. */
export function pvpTeamSlots(ruleset: PvpRuleset): readonly PvpTeamSlot[] {
  return ruleset === 'duel_1v1' ? [1] : PVP_TEAM_SLOTS;
}

/** How many participants a match of this shape needs before it may start. */
export function pvpRosterSize(ruleset: PvpRuleset): 2 | 6 {
  return ruleset === 'duel_1v1' ? 2 : 6;
}

/**
 * Whether a slot exists under a ruleset.
 *
 * The load-bearing half of this is the NEGATIVE: a 1x1 has no slot 2 and no
 * slot 3, and a row claiming one is a row that should never have been written.
 * The database says the same thing in a CHECK; this is what says it before the
 * database has to.
 */
export function pvpIsValidSlot(ruleset: PvpRuleset, slot: number): slot is PvpTeamSlot {
  return Number.isInteger(slot) && slot >= 1 && slot <= pvpSlotsPerTeam(ruleset);
}

/* -------------------------------------------------------------------------- */
/*  Participants                                                              */
/* -------------------------------------------------------------------------- */

/**
 * What the authority calls one participant, everywhere.
 *
 * `a1`, `a2`, `a3`, `b1`, `b2`, `b3`. Derived from team and slot, so it is the
 * same string in the lobby, in the signed ticket, on the socket's attachment,
 * inside the snapshot, after a reconnection, in every frame, in the result and
 * in the audit trail - without any of them having to store a mapping.
 */
export type PvpParticipantId = string;

export function pvpParticipantId(team: PvpTeam, slot: PvpTeamSlot): PvpParticipantId {
  return `${team}${slot}`;
}

/** Every participant id a ruleset can produce, in a stable order. */
export function pvpParticipantIds(ruleset: PvpRuleset): readonly PvpParticipantId[] {
  const ids: PvpParticipantId[] = [];
  for (const team of PVP_TEAMS) {
    for (const slot of pvpTeamSlots(ruleset)) {
      ids.push(pvpParticipantId(team, slot));
    }
  }
  return ids;
}

/**
 * A participant, with everything that identifies them and nothing that changes.
 *
 * No health, no position, no readiness and no connection: those all move during
 * a match, and a reference that carried them would be a reference somebody had
 * to keep fresh. What is here is what the server decided when they joined and
 * will still be true when the match is in a history table.
 */
export interface PvpParticipantRef {
  readonly participantId: PvpParticipantId;
  readonly accountId: string;
  readonly team: PvpTeam;
  readonly slot: PvpTeamSlot;
}

/** Reads a participant id back into a team and a slot, or `null` if it is not one. */
export function pvpParseParticipantId(
  id: string,
  ruleset: PvpRuleset,
): { readonly team: PvpTeam; readonly slot: PvpTeamSlot } | null {
  const match = /^([ab])([1-9])$/.exec(id);
  if (!match) {
    return null;
  }
  const team = pvpParseTeam(match[1] as string);
  const slot = Number(match[2]);
  return team !== null && pvpIsValidSlot(ruleset, slot) ? { team, slot } : null;
}

/**
 * Two participants are the same person, or the same chair.
 *
 * Both are refused at the door, and for different reasons: one account in two
 * slots is one pair of hands driving two ships, and two accounts in one slot is
 * a slot that was handed out twice. The lobby enforces both, the database
 * enforces both, and this is the sentence they are enforcing.
 */
export function pvpRosterConflict(
  roster: readonly PvpParticipantRef[],
  joining: PvpParticipantRef,
): 'slot_taken' | 'account_already_in' | null {
  for (const existing of roster) {
    if (existing.participantId === joining.participantId) {
      return 'slot_taken';
    }
    if (existing.accountId === joining.accountId) {
      return 'account_already_in';
    }
  }
  return null;
}

/** Everyone on one side. Order follows the slot, never the array. */
export function pvpTeamRoster(
  roster: readonly PvpParticipantRef[],
  team: PvpTeam,
): readonly PvpParticipantRef[] {
  return roster.filter((ref) => ref.team === team).sort((left, right) => left.slot - right.slot);
}

/* -------------------------------------------------------------------------- */
/*  The 1x1, mapped                                                           */
/* -------------------------------------------------------------------------- */

/**
 * The shipped duel's seat, as a participant.
 *
 * `a` is team A slot 1 and `b` is team B slot 1 - the whole correspondence,
 * written once. Every other place that needs it imports this rather than
 * concatenating a letter and a one, because the day a 1x1 grows a second slot
 * this is the file that has to refuse.
 */
export function pvpSeatToRef(seat: PvpSeat, accountId: string): PvpParticipantRef {
  const team = asTeam(seat);
  return { participantId: pvpParticipantId(team, 1), accountId, team, slot: 1 };
}

/**
 * A participant back to a 1x1 seat, or `null` when there is no such thing.
 *
 * `null` for slot 2 and slot 3, deliberately and loudly: a team match has no
 * seat, and a caller that wanted one for `a2` is a caller about to write a
 * `winner_seat` that means nothing. See {@link PvpOutcome}.
 *
 * One of exactly two functions that turn a team back into a seat. The cast is
 * safe because the slot has already been checked - which is the whole point of
 * routing the conversion through a function rather than letting the assignment
 * happen wherever somebody needs it.
 */
export function pvpRefToSeat(ref: {
  readonly team: PvpTeam;
  readonly slot: PvpTeamSlot;
}): PvpSeat | null {
  return ref.slot === 1 ? (ref.team as PvpSeat) : null;
}

/* -------------------------------------------------------------------------- */
/*  How a match ended                                                         */
/* -------------------------------------------------------------------------- */

/**
 * The version this build writes and reads.
 *
 * Bumped when the MEANING of a field changes, never when a value is added. A
 * reader that meets a higher version refuses rather than guessing, which is the
 * same rule `PVP_APPEARANCE_VERSION` follows and for the same reason: a result
 * is the one thing a player will argue about.
 */
export const PVP_OUTCOME_VERSION = 1;

/**
 * How a match ended, as one discriminated value.
 *
 * ## Why `winnerSeat` was not simply widened
 *
 * `pvp_matches.winner_seat` is `'a' | 'b' | null`, and null already means two
 * different things: a draw, and a result nobody should trust. Adding "or a
 * team" to that column would make one nullable string carry three questions -
 * who won, whether anybody did, and whether the answer is meaningful - and a
 * reader would have to consult `end_reason` to tell them apart. It already
 * does, which is exactly the ambiguity worth ending rather than extending.
 *
 * So the outcome is a value with a KIND. A 1x1 that somebody won answers
 * `winner` with team `a`, which is the same fact `winner_seat = 'a'` recorded
 * and can be written back to that column unchanged; a 3x3 answers `winner` with
 * a team and no seat at all. A draw and a service failure are their own kinds
 * rather than two spellings of null.
 */
export type PvpOutcome =
  | { readonly v: number; readonly kind: 'winner'; readonly team: PvpTeam }
  /** Nobody won, and that is a real answer: the timer ran out, or both died. */
  | { readonly v: number; readonly kind: 'draw' }
  /**
   * There is no trustworthy answer. A service failure NEVER names a winner, and
   * this is how it says so without borrowing the draw's meaning.
   */
  | { readonly v: number; readonly kind: 'no_result' };

export function pvpWinner(team: PvpTeam): PvpOutcome {
  return { v: PVP_OUTCOME_VERSION, kind: 'winner', team };
}

export function pvpDraw(): PvpOutcome {
  return { v: PVP_OUTCOME_VERSION, kind: 'draw' };
}

export function pvpNoResult(): PvpOutcome {
  return { v: PVP_OUTCOME_VERSION, kind: 'no_result' };
}

/**
 * The outcome as the column that exists today would store it - for a 1x1 ONLY.
 *
 * ## Why the ruleset is a parameter and not an afterthought
 *
 * `winner_seat` names a PERSON. In a 1x1 the winning team has exactly one, so
 * writing the team letter into it records the same fact it always recorded. In
 * a 3x3 the winning team has three, and none of them is "seat a" - so the
 * honest answer is `null` and the winner is recorded as a TEAM elsewhere.
 *
 * This is the second of the two places that turn a team back into a seat, and
 * like {@link pvpRefToSeat} it refuses rather than converting whenever the
 * answer would be a lie. That refusal is what the brand on `PvpTeam` cannot
 * express: an intersection is assignable to its constituents, so nothing stops
 * a team being passed where a seat is wanted - only a function that declines to
 * do it does.
 *
 * The loss of detail for a draw is safe and always was: `end_reason` is stored
 * beside this column and carries the distinction, which is why
 * {@link pvpOutcomeFromRow} reads the pair rather than the column alone.
 */
export function pvpOutcomeToWinnerSeat(outcome: PvpOutcome, ruleset: PvpRuleset): PvpSeat | null {
  if (outcome.kind !== 'winner' || ruleset !== 'duel_1v1') {
    return null;
  }
  return outcome.team as PvpSeat;
}

/**
 * The outcome a stored row describes, from the pair rather than from one column.
 *
 * This is what keeps a 1x1 history readable after the vocabulary changed: rows
 * written before any of this existed still say what they always said, because
 * the pair of columns they were written with is the pair being read.
 */
export function pvpOutcomeFromRow(row: {
  readonly winnerSeat: string | null;
  readonly endReason: string | null;
}): PvpOutcome {
  const winner = row.winnerSeat === null ? null : pvpParseTeam(row.winnerSeat);
  if (winner !== null) {
    return pvpWinner(winner);
  }
  return row.endReason === 'service_failure' ? pvpNoResult() : pvpDraw();
}

/** Whether a build may read an outcome somebody else wrote. */
export function pvpOutcomeIsReadable(outcome: { readonly v: number }): boolean {
  return outcome.v <= PVP_OUTCOME_VERSION;
}
